"use client";

import _slicedToArray from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _createForOfIteratorHelper from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import { FocusableContext } from "./SWN3JYXT.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useEvent, useMergeRefs, useTagName } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js"; // src/focusable/focusable.tsx

import { isButton } from "@ariakit/core/utils/dom";
import { addGlobalEventListener, isFocusEventOutside, isPortalEvent, isSelfTarget, queueBeforeEvent } from "@ariakit/core/utils/events";
import { focusIfNeeded, getClosestFocusable, hasFocus, isFocusable } from "@ariakit/core/utils/focus";
import { disabledFromProps, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { isSafari } from "@ariakit/core/utils/platform";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
var TagName = "div";
var isSafariBrowser = isSafari();
var alwaysFocusVisibleInputTypes = ["text", "search", "url", "tel", "email", "password", "number", "date", "month", "week", "time", "datetime", "datetime-local"];
var safariFocusAncestorSymbol = Symbol("safariFocusAncestor");

function isSafariFocusAncestor(element) {
  if (!element) return false;
  return !!element[safariFocusAncestorSymbol];
}

function markSafariFocusAncestor(element, value) {
  if (!element) return;
  element[safariFocusAncestorSymbol] = value;
}

function isAlwaysFocusVisible(element) {
  var tagName = element.tagName,
      readOnly = element.readOnly,
      type = element.type;
  if (tagName === "TEXTAREA" && !readOnly) return true;
  if (tagName === "SELECT" && !readOnly) return true;

  if (tagName === "INPUT" && !readOnly) {
    return alwaysFocusVisibleInputTypes.includes(type);
  }

  if (element.isContentEditable) return true;
  var role = element.getAttribute("role");

  if (role === "combobox" && element.dataset.name) {
    return true;
  }

  return false;
}

function getLabels(element) {
  if ("labels" in element) {
    return element.labels;
  }

  return null;
}

function isNativeCheckboxOrRadio(element) {
  var tagName = element.tagName.toLowerCase();

  if (tagName === "input" && element.type) {
    return element.type === "radio" || element.type === "checkbox";
  }

  return false;
}

function isNativeTabbable(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "summary" || tagName === "input" || tagName === "select" || tagName === "textarea" || tagName === "a";
}

function supportsDisabledAttribute(tagName) {
  if (!tagName) return true;
  return tagName === "button" || tagName === "input" || tagName === "select" || tagName === "textarea";
}

function getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, tabIndexProp) {
  if (!focusable) {
    return tabIndexProp;
  }

  if (trulyDisabled) {
    if (nativeTabbable && !supportsDisabled) {
      return -1;
    }

    return;
  }

  if (nativeTabbable) {
    return tabIndexProp;
  }

  return tabIndexProp || 0;
}

function useDisableEvent(onEvent, disabled) {
  return useEvent(function (event) {
    onEvent == null ? void 0 : onEvent(event);
    if (event.defaultPrevented) return;

    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  });
}

var isKeyboardModality = true;

function onGlobalMouseDown(event) {
  var target = event.target;

  if (target && "hasAttribute" in target) {
    if (!target.hasAttribute("data-focus-visible")) {
      isKeyboardModality = false;
    }
  }
}

function onGlobalKeyDown(event) {
  if (event.metaKey) return;
  if (event.ctrlKey) return;
  if (event.altKey) return;
  isKeyboardModality = true;
}

var useFocusable = createHook(function useFocusable2(_a) {
  var _b = _a,
      _b$focusable = _b.focusable,
      focusable = _b$focusable === void 0 ? true : _b$focusable,
      accessibleWhenDisabled = _b.accessibleWhenDisabled,
      autoFocus = _b.autoFocus,
      onFocusVisible = _b.onFocusVisible,
      props = __objRest(_b, ["focusable", "accessibleWhenDisabled", "autoFocus", "onFocusVisible"]);

  var ref = useRef(null);
  useEffect(function () {
    if (!focusable) return;
    addGlobalEventListener("mousedown", onGlobalMouseDown, true);
    addGlobalEventListener("keydown", onGlobalKeyDown, true);
  }, [focusable]);

  if (isSafariBrowser) {
    useEffect(function () {
      if (!focusable) return;
      var element = ref.current;
      if (!element) return;
      if (!isNativeCheckboxOrRadio(element)) return;
      var labels = getLabels(element);
      if (!labels) return;

      var onMouseUp = function onMouseUp() {
        return queueMicrotask(function () {
          return element.focus();
        });
      };

      var _iterator = _createForOfIteratorHelper(labels),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var label = _step.value;
          label.addEventListener("mouseup", onMouseUp);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      return function () {
        var _iterator2 = _createForOfIteratorHelper(labels),
            _step2;

        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var label = _step2.value;
            label.removeEventListener("mouseup", onMouseUp);
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      };
    }, [focusable]);
  }

  var disabled = focusable && disabledFromProps(props);
  var trulyDisabled = !!disabled && !accessibleWhenDisabled;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focusVisible = _useState2[0],
      setFocusVisible = _useState2[1];

  useEffect(function () {
    if (!focusable) return;

    if (trulyDisabled && focusVisible) {
      setFocusVisible(false);
    }
  }, [focusable, trulyDisabled, focusVisible]);
  useEffect(function () {
    if (!focusable) return;
    if (!focusVisible) return;
    var element = ref.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    var observer = new IntersectionObserver(function () {
      if (!isFocusable(element)) {
        setFocusVisible(false);
      }
    });
    observer.observe(element);
    return function () {
      return observer.disconnect();
    };
  }, [focusable, focusVisible]);
  var onKeyPressCapture = useDisableEvent(props.onKeyPressCapture, disabled);
  var onMouseDownCapture = useDisableEvent(props.onMouseDownCapture, disabled);
  var onClickCapture = useDisableEvent(props.onClickCapture, disabled);
  var onMouseDownProp = props.onMouseDown;
  var onMouseDown = useEvent(function (event) {
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    var element = event.currentTarget;
    if (!isSafariBrowser) return;
    if (isPortalEvent(event)) return;
    if (!isButton(element) && !isNativeCheckboxOrRadio(element)) return;
    var receivedFocus = false;

    var onFocus = function onFocus() {
      receivedFocus = true;
    };

    var options = {
      capture: true,
      once: true
    };
    element.addEventListener("focusin", onFocus, options);
    var focusableContainer = getClosestFocusable(element.parentElement);
    markSafariFocusAncestor(focusableContainer, true);
    queueBeforeEvent(element, "mouseup", function () {
      element.removeEventListener("focusin", onFocus, true);
      markSafariFocusAncestor(focusableContainer, false);
      if (receivedFocus) return;
      focusIfNeeded(element);
    });
  });

  var handleFocusVisible = function handleFocusVisible(event, currentTarget) {
    if (currentTarget) {
      event.currentTarget = currentTarget;
    }

    if (!focusable) return;
    var element = event.currentTarget;
    if (!element) return;
    if (!hasFocus(element)) return;
    onFocusVisible == null ? void 0 : onFocusVisible(event);
    if (event.defaultPrevented) return;
    element.dataset.focusVisible = "true";
    setFocusVisible(true);
  };

  var onKeyDownCaptureProp = props.onKeyDownCapture;
  var onKeyDownCapture = useEvent(function (event) {
    onKeyDownCaptureProp == null ? void 0 : onKeyDownCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;
    if (focusVisible) return;
    if (event.metaKey) return;
    if (event.altKey) return;
    if (event.ctrlKey) return;
    if (!isSelfTarget(event)) return;
    var element = event.currentTarget;

    var applyFocusVisible = function applyFocusVisible() {
      return handleFocusVisible(event, element);
    };

    queueBeforeEvent(element, "focusout", applyFocusVisible);
  });
  var onFocusCaptureProp = props.onFocusCapture;
  var onFocusCapture = useEvent(function (event) {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!focusable) return;

    if (!isSelfTarget(event)) {
      setFocusVisible(false);
      return;
    }

    var element = event.currentTarget;

    var applyFocusVisible = function applyFocusVisible() {
      return handleFocusVisible(event, element);
    };

    if (isKeyboardModality || isAlwaysFocusVisible(event.target)) {
      queueBeforeEvent(event.target, "focusout", applyFocusVisible);
    } else {
      setFocusVisible(false);
    }
  });
  var onBlurProp = props.onBlur;
  var onBlur = useEvent(function (event) {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (!focusable) return;
    if (!isFocusEventOutside(event)) return;
    setFocusVisible(false);
  });
  var autoFocusOnShow = useContext(FocusableContext);
  var autoFocusRef = useEvent(function (element) {
    if (!focusable) return;
    if (!autoFocus) return;
    if (!element) return;
    if (!autoFocusOnShow) return;
    queueMicrotask(function () {
      if (hasFocus(element)) return;
      if (!isFocusable(element)) return;
      element.focus();
    });
  });
  var tagName = useTagName(ref);
  var nativeTabbable = focusable && isNativeTabbable(tagName);
  var supportsDisabled = focusable && supportsDisabledAttribute(tagName);
  var styleProp = props.style;
  var style = useMemo(function () {
    if (trulyDisabled) {
      return __spreadValues({
        pointerEvents: "none"
      }, styleProp);
    }

    return styleProp;
  }, [trulyDisabled, styleProp]);
  props = __spreadProps(__spreadValues({
    "data-focus-visible": focusable && focusVisible || void 0,
    "data-autofocus": autoFocus || void 0,
    "aria-disabled": disabled || void 0
  }, props), {
    ref: useMergeRefs(ref, autoFocusRef, props.ref),
    style: style,
    tabIndex: getTabIndex(focusable, trulyDisabled, nativeTabbable, supportsDisabled, props.tabIndex),
    disabled: supportsDisabled && trulyDisabled ? true : void 0,
    // TODO: Test Focusable contentEditable.
    contentEditable: disabled ? void 0 : props.contentEditable,
    onKeyPressCapture: onKeyPressCapture,
    onClickCapture: onClickCapture,
    onMouseDownCapture: onMouseDownCapture,
    onMouseDown: onMouseDown,
    onKeyDownCapture: onKeyDownCapture,
    onFocusCapture: onFocusCapture,
    onBlur: onBlur
  });
  return removeUndefinedValues(props);
});
var Focusable = forwardRef(function Focusable2(props) {
  var htmlProps = useFocusable(props);
  return createElement(TagName, htmlProps);
});
export { isSafariFocusAncestor, useFocusable, Focusable };